import React, { useState, useMemo, useCallback } from 'react';

import ReactLightbox from 'react-image-lightbox';
import css from './Lightbox.module.scss';

export interface LightboxImage {
  thumbUrl: string;
  mainUrl: string;
}

interface Props {
  className?: string;
  images: LightboxImage[];
  alt?: string;
}

const Lightbox: React.FC<Props> = ({ images, alt = '' }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);

  const currentPhoto = useMemo(() => {
    return images[currentPhotoIndex];
  }, [images, currentPhotoIndex]);

  const onClickOpen = useCallback((index: number) => {
    setIsOpen(true);
    setCurrentPhotoIndex(index);
  }, []);

  const onMovePrevRequest = useCallback(() => {
    const prev =
      currentPhotoIndex - 1 < 0 ? images.length - 1 : currentPhotoIndex - 1;
    setCurrentPhotoIndex(prev);
  }, [images, currentPhotoIndex]);

  const onMoveNextRequest = useCallback(() => {
    const next =
      currentPhotoIndex + 1 >= images.length ? 0 : currentPhotoIndex + 1;
    setCurrentPhotoIndex(next);
  }, [images, currentPhotoIndex]);

  if (images.length === 0) {
    return null;
  }

  return (
    <div className={css.Lightbox}>
      <div className={css.Lightbox__List}>
        {images.map((image, i) => (
          <button
            key={image.mainUrl}
            className={css.Lightbox__Button}
            type="button"
            onClick={() => onClickOpen(i)}
          >
            <img
              className={css.Lightbox__Thumbnail}
              src={image.thumbUrl}
              alt={alt}
            />
          </button>
        ))}
      </div>
      {isOpen && (
        <ReactLightbox
          mainSrc={currentPhoto.mainUrl}
          prevSrc={
            (images[currentPhotoIndex - 1] || images[images.length - 1]).mainUrl
          }
          nextSrc={(images[currentPhotoIndex + 1] || images[0]).mainUrl}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() => onMovePrevRequest()}
          onMoveNextRequest={() => onMoveNextRequest()}
          enableZoom={false}
        />
      )}
    </div>
  );
};

export default Lightbox;
